import type { ItemPollingResponseDto, PeakInfo, PollingRequestDto, PollingResponseDto } from '@mimir/common/dto/pollingDtos';
import type { ItemPollingOps, ItemPollingResponse } from './itemPollingOps';
import type { Logger } from '@mimir/common/types/logger';
import type { FetchedPeaks, PeaksFetcherOps } from './peaksFetcherOps';
import type { Clock } from '@mimir/common/types/clock';
import { exceptionToString } from '@mimir/common/util/exception';
import { createUuid } from '@mimir/common/util/createUuid';
import { invariant } from '@mimir/common/util/invariant';
import { buildItemPollingResponse, getItemPollingPlan, hasNewPeaks, itemNeedsMorePolling } from './itemPollingUtils';
import type { ItemBookkeeping, PollingPlan, PollingResponse, ItemFullPollingResponse, ListenerInfo } from './itemPollingTypes';


export const ITEM_POLLING_LOOP_TICK_IN_MS = 2000;


export class ItemPollingService implements ItemPollingOps {

  private polling = false;
  private bookkeeping: Record<string, ItemBookkeeping> = {};

  constructor(
    private logger: Logger,
    private poll: (request: PollingRequestDto) => Promise<PollingResponseDto>,
    private peaksFetcherFactory: (itemId: string, peakInfo: PeakInfo) => PeaksFetcherOps,
    private clock: Clock,
  ) {}


  isPolling(): boolean {
    return this.polling;
  }


  /**
   * A promise-based wait function using the injected clock,
   * so that we can control this from tests.
   */
  private wait(timeoutInMs: number): Promise<void> {
    return new Promise<void>((resolve, _reject) => {
      this.clock.setTimeout(() => resolve(), timeoutInMs);
    });
  }


  private startPollingThreadIfNeeded() {
    if (this.polling) {
      return;
    }

    void this.runPollingLoop();
  }


  /**
   * We only need to poll when there are listeners waiting for something.
   */
  private shouldPoll(): boolean {
    return Object.keys(this.bookkeeping).length !== 0;
  }


  /**
   * Runs the polling loop until there are no more listeners
   * or all polling operations have stopped because none of the
   * items is changing anymore.
   */
  private async runPollingLoop(): Promise<void> {
    this.polling = true;

    // If the polling loop was not running and someone adds a listener,
    // it should start. However, in case the caller wants to add several
    // listeners, it is better to wait a tiny bit so that the caller
    // can add all its listeners before the first poll actually occurs.
    // Otherwise, the first listener added would trigger an immediate poll
    // just for this listener and the remaining ones would be taken into
    // account only in the next iteration of the loop
    await this.wait(1);

    while (this.shouldPoll()) {
      const shouldPollAgainImmediately = await this.pollForAllItems().catch(err => {
        this.logger.error(`Failed to poll: ${exceptionToString(err)}`);
      });
      if (shouldPollAgainImmediately !== true) {
        await this.wait(ITEM_POLLING_LOOP_TICK_IN_MS);
      }
    }
    this.polling = false;
  }


  /**
   * Polls the backend to get all the information needed to satisfy all the poll
   * listeners. Once the information is obtained, the responses are dispatched
   * to all listeners, which includes removing listeners for items that do
   * not need any more polling.
   *
   * Returns true if we should poll again immediately.
   */
  private async pollForAllItems(): Promise<boolean> {
    const itemIds = Object.keys(this.bookkeeping);
    const pollingPlan: PollingPlan = {};

    for (const itemId of itemIds) {
      const bookkeeping = this.bookkeeping[itemId];
      const state = {
        hasPeakInfo: bookkeeping.peakInfo !== undefined,
        peaksComplete: bookkeeping.fetchedPeaks?.complete ?? false,
        hasAudioProxyUrls: bookkeeping.proxyAudioTrackUrls !== undefined,
      };
      pollingPlan[itemId] = getItemPollingPlan(bookkeeping.listeners, state);
    }

    const pollingResponse = await this.executePollingPlan(pollingPlan);

    let shouldPollImmediately = false;
    for (const itemId of itemIds) {
      if (pollingResponse[itemId] === undefined) {
        // If some error (like failing to obtain peaks) has prevented the
        // polling plan to be executed for an item, we skip it
        this.logger.warn(`Skipping update for item ${itemId}`);
        continue;
      }
      if (this.processItemPollingResponse(itemId, pollingResponse[itemId], this.bookkeeping[itemId])) {
        shouldPollImmediately = true;
      }
    }

    return shouldPollImmediately;
  }


  /**
   * Fetches peaks for the given item ID and stores them
   * in the item's bookkeeping object. This function will
   * create the peaks fetcher if needed.
   */
  private async fetchAndStorePeaks(itemId: string): Promise<void> {
    const bookkeeping = this.bookkeeping[itemId];
    const peakInfo = bookkeeping.peakInfo;
    invariant(peakInfo !== undefined, `Should not invoke fetchAndStorePeaks to fetch peaks for item ${itemId} without peakInfo`);

    if (bookkeeping.peakFetcher === undefined) {
      bookkeeping.peakFetcher = this.peaksFetcherFactory(itemId, peakInfo);
    }

    try {
      const freshPeaks = await bookkeeping.peakFetcher.fetchPeaks();
      const hasFreshPeaks = hasNewPeaks(bookkeeping.fetchedPeaks, freshPeaks);
      if (hasFreshPeaks) {
        bookkeeping.lastNewPeaks = this.clock.monotonicNow();
      }
      bookkeeping.fetchedPeaks = freshPeaks;
    } catch (error: any) {
      this.logger.error(`Could not fetch peaks for item ${itemId}: ${exceptionToString(error)}`);
      if (error?.info?.code === 404) {
        if (bookkeeping.fetchedPeaks) {
          // If we had peaks, let's mark them as complete
          bookkeeping.fetchedPeaks.complete = true;
        } else {
          // Otherwise, let's create empty peaks and mark them as complete
          const peaksPerChannel: Uint8Array[] = [];
          for (let i = 0; i < peakInfo.nAudioChannels; i++) {
            peaksPerChannel.push(new Uint8Array(0));
          }
          bookkeeping.fetchedPeaks = {
            peaksPerChannel,
            complete: true,
          };
        }
      }
    }
  }


  /**
   * Fetches all the data needed to satisfy all poll listeners.
   */
  private async executePollingPlan(pollingPlan: PollingPlan): Promise<PollingResponse> {
    const itemIds = Object.keys(pollingPlan);
    const request: PollingRequestDto = {};
    const peakPromises: Promise<void>[] = [];

    for (const itemId of itemIds) {
      request[itemId] = pollingPlan[itemId].keys;
      if (pollingPlan[itemId].shouldFetchPeaks) {
        peakPromises.push(this.fetchAndStorePeaks(itemId));
      }
    }

    const [pollingResponseDto] = await Promise.all([this.poll(request), ...peakPromises]);

    const pollingResponse: PollingResponse = {};
    for (const itemId of itemIds) {
      const itemPollingResponse = pollingResponseDto[itemId] ?? null;

      let fetchedPeaks: FetchedPeaks | null = null;
      if (pollingPlan[itemId].shouldFetchPeaks) {
        const peaks = this.bookkeeping[itemId].fetchedPeaks;
        if (peaks === undefined) {
          this.logger.error(`Did not get peaks for item ${itemId}`);
          continue;
        }
        fetchedPeaks = peaks;
      }

      pollingResponse[itemId] = {
        itemPollingResponse,
        fetchedPeaks,
      };
    }
    return pollingResponse;
  }


  /**
   * The fetchedPeaks property is updated directly in fetchAndStorePeaks().
   *
   * Properties that need to be updated from the ItemPollingResponseDto, if any,
   * are taken care of here.
   */
  private updateBookkeeping(
    itemPollingResponse: ItemPollingResponseDto | null,
    bookkeeping: ItemBookkeeping,
  ): void {
    if (bookkeeping.peakInfo === undefined && itemPollingResponse?.peakInfo) {
      bookkeeping.peakInfo = itemPollingResponse.peakInfo;
    }

    if (bookkeeping.proxyAudioTrackUrls === undefined && itemPollingResponse?.proxyAudioTrackUrls) {
      bookkeeping.proxyAudioTrackUrls = itemPollingResponse.proxyAudioTrackUrls;
    }
  }


  /**
   * This function dispatches the given response to all the listeners for the given item.
   * It then clears the polling for the item if the response indicates that the item is either
   * gone or done growing.
   *
   * Returns true if we should initiate immediately a new poll, i.e. without waiting for the
   * regular tick period.
   */
  private processItemPollingResponse(
    itemId: string,
    itemFullPollingResponse: ItemFullPollingResponse,
    bookkeeping: ItemBookkeeping,
  ): boolean {
    this.updateBookkeeping(itemFullPollingResponse.itemPollingResponse, bookkeeping);

    let shouldStartFetchingPeaks = false;
    if (bookkeeping.peakInfo !== undefined && bookkeeping.peakFetcher === undefined) {
      // If someone had requested peaks and we get some peak info, we want to start fetching peaks.
      // However, we don't have peaks yet, but we want to hint the caller that peaks are available.
      // So, we patch itemFullPollingResponse.fetchedPeaks to inject empty peaks arrays to make the caller
      // aware of peak availability, so that they can for instance not try to load peaks from audio proxies.
      //
      // Also, we create the peakFetcher immediately so that the condition for requesting immediately
      // a new poll will never be triggered more than once for a given item
      shouldStartFetchingPeaks = true;
      bookkeeping.peakFetcher = this.peaksFetcherFactory(itemId, bookkeeping.peakInfo);
      invariant(itemFullPollingResponse.fetchedPeaks === null, () => `Should not have peaks already for item ${itemId}`);
      itemFullPollingResponse.fetchedPeaks = {
        complete: false,
        peaksPerChannel: [],
      };
      for (let i = 0; i < bookkeeping.peakInfo.nAudioChannels; i++) {
        itemFullPollingResponse.fetchedPeaks.peaksPerChannel.push(new Uint8Array(0));
      }
    }

    const complete = !shouldStartFetchingPeaks && !itemNeedsMorePolling(itemFullPollingResponse, this.clock.monotonicNow(), bookkeeping);

    for (const listener of bookkeeping.listeners) {
      const itemPollingResponse = buildItemPollingResponse(itemId, listener.requestedProperties, itemFullPollingResponse, bookkeeping);
      listener.callback(itemPollingResponse, complete);
    }

    if (complete) {
      this.clearPollingForItem(itemId);
    }

    return shouldStartFetchingPeaks;
  }


  addPollListener<K extends keyof ItemPollingResponse>(
    itemId: string,
    requestedProperties: K[],
    callback: (response: null | Pick<ItemPollingResponse, K>, pollingStopped: boolean) => void,
  ): string {
    const listenerId = createUuid();
    const listenerInfo: ListenerInfo<K> = {
      listenerId,
      requestedProperties,
      callback,
    };

    let bookkeeping = this.bookkeeping[itemId];
    if (bookkeeping === undefined) {
      bookkeeping = {
        listeners: [],
        peakInfo: undefined,
        peakFetcher: undefined,
        fetchedPeaks: undefined,
        lastNewPeaks: undefined,
        hasBeenStreaming: false,
        proxyAudioTrackUrls: undefined,
      };
      this.bookkeeping[itemId] = bookkeeping;
    }

    bookkeeping.listeners.push(listenerInfo);

    this.startPollingThreadIfNeeded();
    return listenerId;
  }


  removePollListener(listenerId: string): void {
    for (const itemId of Object.keys(this.bookkeeping)) {
      const listeners = this.bookkeeping[itemId].listeners;
      for (let i = 0; i < listeners.length; i++) {
        if (listeners[i].listenerId === listenerId) {
          listeners.splice(i, 1);
          if (listeners.length === 0) {
            // There are no more listeners for this item
            this.clearPollingForItem(itemId);
          }
          return;
        }
      }
    }
  }


  private clearPollingForItem(itemId: string) {
    delete this.bookkeeping[itemId];
  }

}
